import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { fetchGetData, fetchPostData, fetchFormData } from './api'; // Import your common API file

const TrackEmployee = () => {
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [name, setName] = useState('');
    const [mobile, setMobile] = useState('');
    const [district, setDistrict] = useState('');
    const [selectedArea, setSelectedArea] = useState('');
    const [jobRole, setJobRole] = useState('');
    const [qualification, setQualification] = useState('');
    const [experience, setExperience] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [gender, setGender] = useState('');
    const [preferredLocation, setPreferredLocation] = useState('');

    const fileInputRef = useRef(null);

    const [districts, setDistricts] = useState([]);
    const [jobRoles, setJobRoles] = useState([]);
    const [areas, setAreas] = useState([]);

    // Fetch data on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [districtRes, jobRoleRes] = await Promise.all([
                    fetchGetData('/master/get/district/city'), // Replace with actual API
                    fetchGetData('/master/get/title')    // Replace with actual API
                ]);
                setDistricts(districtRes.data);
                setJobRoles(jobRoleRes.data);
            } catch (error) {
                console.error('Error fetching data:', error);
                setErrorMessage('Error loading district and job role data.');
            }
        };

        fetchData();
    }, []);

    const handleDistrictChange = (e) => {
        const selectedDistrictId = e.target.value;
        setDistrict(selectedDistrictId);
        setAreas(''); // Reset selected area

        // Find the selected district from the districts array
        const selectedDistrict = districts.find(d => d.id === Number(selectedDistrictId));

        // If district is found, set its areas
        if (selectedDistrict) {
            setAreas(selectedDistrict.areas); // Set areas from the selected district
        } else {
            setAreas([]); // Reset areas if no district is selected
        }
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        setFile(selectedFile ? selectedFile : null);
    };

    const validateForm = () => {
        if (!name.trim()) return "Name is required.";
        if (!mobile.trim()) return "Mobile number is required.";
        if (!/^[6789][0-9]{9}$/.test(mobile)) return "Mobile number must be exactly 10 digits and start with 6, 7, 8, or 9.";
        if (!district) return "District must be selected.";
        if (!jobRole) return "Job role must be selected.";
        if (!selectedArea) return "Area must be selected.";
        if (!qualification) return "Qualification is required.";
        if (!startDate || isNaN(new Date(startDate))) return "Valid DOB is required.";
        if (!gender) return "Gender must be selected.";
        if (!preferredLocation) return "Preferred location must be selected.";
        if (!file) return "Please upload a file.";
        return null;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }

        // Create a new FormData object and append all form fields to it
        const formData = new FormData();
        formData.append('file', file); // The file upload field
        formData.append('name', name);
        formData.append('mobile', mobile);
        formData.append('district', district);
        formData.append('jobRole', jobRole);
        formData.append('selectedArea', selectedArea);
        formData.append('qualification', qualification);
        formData.append('experience', experience);
        formData.append('companyName', companyName);
        formData.append('startDate', startDate);
        formData.append('gender', gender);
        formData.append('preferredLocation', preferredLocation);

        setLoading(true);

        try {
            const response = await fetchFormData('/employee/create', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.success) {
                setSuccessMessage('Form submitted and processed successfully!');
                setFile(null);
                setName('');
                setMobile('');
                setDistrict('');
                setJobRole('');
                setSelectedArea('');
                setQualification('');
                setExperience('');
                setCompanyName('');
                setStartDate('');
                setGender('');
                setPreferredLocation('');

                // Clear file input field
                if (fileInputRef.current) {
                    fileInputRef.current.value = null;
                }
            } else {
                setErrorMessage('Form submission failed.');
            }
        } catch (error) {
            console.error('Error submitting the form:', error);
            // setErrorMessage('An error occurred while submitting the form.');
            setErrorMessage(JSON.stringify(error.message));
        } finally {
            setLoading(false);
        }

        setTimeout(() => {
            setErrorMessage(''); // Clear error message
            setSuccessMessage(''); // Clear success message
        }, 5000);
    };

    return (
        <div className="container mt-5">
            <Card className="p-4 shadow-sm">
                <h4 className="mb-4">Create employee</h4>
                {loading && (
                    <div className="text-center my-4">
                        <Spinner animation="border" />
                    </div>
                )}
                <Form onSubmit={handleSubmit}>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group controlId="name">
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    minLength={5}
                                    maxLength={15}
                                    required
                                    placeholder="Enter name"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="mobile">
                                <Form.Label>Mobile</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={mobile}
                                    onChange={(e) => setMobile(e.target.value)}
                                    pattern="^[6789][0-9]{9}$"
                                    required
                                    placeholder="Enter mobile number"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="district">
                                <Form.Label>District</Form.Label>
                                <Form.Select
                                    value={district}
                                    onChange={handleDistrictChange}
                                    required
                                >
                                    <option value="">Select District</option>
                                    {districts.map(district => (
                                        <option key={district.id} value={district.id}>
                                            {district.dist}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="area">
                                <Form.Label>Area</Form.Label>
                                <Form.Select
                                    value={selectedArea}
                                    onChange={(e) => setSelectedArea(e.target.value)}
                                    required
                                >
                                    <option value="">Select Area</option>
                                    {areas.map(area => (
                                        <option key={area.id} value={area.id}>
                                            {area.area_name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="jobRole">
                                <Form.Label>Job Role</Form.Label>
                                <Form.Select
                                    value={jobRole}
                                    onChange={(e) => setJobRole(e.target.value)}
                                    required
                                >
                                    <option value="">Select Job Role</option>
                                    {jobRoles.map(role => (
                                        <option key={role.id} value={role.id}>
                                            {role.english}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="qualification">
                                <Form.Label>Qualification</Form.Label>
                                <Form.Select
                                    value={qualification}
                                    onChange={(e) => setQualification(e.target.value)}
                                    required
                                >
                                    <option value="">Select Qualification</option>
                                    <option value="High School">High School</option>
                                    <option value="Bachelor's">Bachelor's</option>
                                    <option value="Master's">Master's</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="experience">
                                <Form.Label>Experience</Form.Label>
                                <Form.Select
                                    value={experience}
                                    onChange={(e) => setExperience(e.target.value)}
                                    required
                                >
                                    <option value="">Select Experience</option>
                                    <option value="Fresher">Fresher</option>
                                    <option value="Experienced">Experienced</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="startDate">
                                <Form.Label>Date of Birth</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max="2024-10-09"
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="gender">
                                <Form.Label>Gender</Form.Label>
                                <div>
                                    <Form.Check
                                        inline
                                        type="radio"
                                        id="male"
                                        name="gender"
                                        label="Male"
                                        value="0"
                                        checked={gender === '0'}
                                        onChange={(e) => setGender(e.target.value)}
                                        required
                                    />
                                    <Form.Check
                                        inline
                                        type="radio"
                                        id="female"
                                        name="gender"
                                        label="Female"
                                        value="1"
                                        checked={gender === '1'}
                                        onChange={(e) => setGender(e.target.value)}
                                        required
                                    />
                                    <Form.Check
                                        inline
                                        type="radio"
                                        id="other"
                                        name="gender"
                                        label="None"
                                        value="NULL"
                                        checked={gender === 'NULL'}
                                        onChange={(e) => setGender(e.target.value)}
                                        required
                                    />
                                </div>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="preferredLocation">
                                <Form.Label>Preferred Location</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={preferredLocation}
                                    onChange={(e) => setPreferredLocation(e.target.value)}
                                    placeholder="Enter preferred location"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="fileUpload">
                                <Form.Label>Resume Upload</Form.Label>
                                <Form.Control
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept=".pdf"
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <div className="text-center">
                        <Button type="submit" variant="primary" disabled={loading}>
                            {loading ? 'Submitting...' : 'Submit'}
                        </Button>
                    </div>
                </Form>
                {errorMessage && (
                    <Alert variant="danger" className="mt-3">
                        {errorMessage}
                    </Alert>
                )}
                {successMessage && (
                    <Alert variant="success" className="mt-3">
                        {successMessage}
                    </Alert>
                )}
            </Card>
        </div>
    );
};

export default TrackEmployee;
